import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import './Automation.css';
import ShipmentList from './ShipmentList';
import DeliveryList from './DeliveryList';
import { API_BASE_URL } from '../config/api';

function excelSerialToDate(serial: number): string {
  
  
  
  const excelEpoch = new Date(1899, 11, 30); 
  const jsDate = new Date(excelEpoch.getTime() + (serial * 24 * 60 * 60 * 1000));
  
  
  const day = jsDate.getDate().toString().padStart(2, '0');
  const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
  const year = jsDate.getFullYear();
  
  console.log(`Excel serial ${serial} converted to: ${day}.${month}.${year}`);
  return `${day}.${month}.${year}`;
}

function normalizeBatchNumber(batchNumber: string | number | Date, useMmDdYyFormat: boolean = false): string {
  if (!batchNumber && batchNumber !== 0) return '';
  
  
  if (typeof batchNumber === 'number' && batchNumber > 30000 && batchNumber < 100000) {
    const dateStr = excelSerialToDate(batchNumber);
    return normalizeBatchNumber(dateStr, useMmDdYyFormat);
  }
  
  
  if (batchNumber instanceof Date) {
    const day = batchNumber.getDate().toString().padStart(2, '0');
    const month = (batchNumber.getMonth() + 1).toString().padStart(2, '0');
    const year = batchNumber.getFullYear().toString().slice(-2);
    return `${day}.${month}.${year}`;
  }
  
  
  const batch = String(batchNumber).trim();
  
  
  const datePattern = /(\d{1,2})([.\-/])(\d{1,2})\2(\d{2,4})/;
  const match = batch.match(datePattern);
  
  if (match) {
    let [, first, separator, second, yearPart] = match;
    
    
    let year = '';
    if (yearPart.length >= 4) {
      year = yearPart.slice(-2); 
    } else {
      year = yearPart.padStart(2, '0');
    }
    
    if (useMmDdYyFormat) {
      
      const month = first.padStart(2, '0');
      const day = second.padStart(2, '0');
      const normalized = `${day}.${month}.${year}`;
      return normalized;
    } else {
      
      const day = first.padStart(2, '0');
      const month = second.padStart(2, '0');
      const normalized = `${day}.${month}.${year}`;
      return normalized;
    }
  }
  
  
  return batch;
}

interface StorageInfo {
  bin_name?: string; 
  storage_name?: string; 
  bin_id?: string; 
  quantity?: number; 
  available_quantity?: number; 
  warehouse_id?: string; 
  warehouse_name?: string; 
  warehouse_total_quantity?: number; 
  optimized_quantity?: number; 
  error?: boolean; 
  
  
  storageId?: string;
  storage_id?: string;
  storageInId?: string;
  storageOutId?: string;
  inQuantity?: number;
  in_quantity?: number;
  outQuantity?: number;
  out_quantity?: number;
  serialNumbers?: string[];
}

interface BatchInfo {
  batchInId?: string;
  batchOutId?: string;
  batchNumber?: string;
  externalBatchNumber?: string;
  manufacturerDate?: string;
  expiryDate?: string;
  inQuantity?: number;
  outQuantity?: number;
  storages?: StorageInfo[];
  selectedQuantity?: number; 
  
  
  batch_number?: string;
  batch_in_id?: string;
  batch_id?: string;
  expiry_date?: string;
  manufacturer_date?: string;
  in_quantity?: number;
  out_quantity?: number;
  balance_quantity?: number;
  status?: string;
}

const Automation: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState({
    itemName: '',
    quantity: '',
    batchNumber: ''
  });
  const [enableBatchTracking, setEnableBatchTracking] = useState(false);
  const [useMmDdYyFormat, setUseMmDdYyFormat] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [itemBatches, setItemBatches] = useState<{ [key: number]: BatchInfo[] }>({});
  const [loadingBatches, setLoadingBatches] = useState<Set<number>>(new Set());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [activeMainTab, setActiveMainTab] = useState<'shipments' | 'deliveries'>('shipments');
  const [activeSubTab, setActiveSubTab] = useState<'list' | 'create'>('list');

  
  const [isCreatingShipment, setIsCreatingShipment] = useState(false);
  const [itemsNeedingManualSelection, setItemsNeedingManualSelection] = useState<Set<number>>(new Set());
  const [itemProcessingStatus, setItemProcessingStatus] = useState<{ [key: number]: 'pending' | 'processing' | 'found' | 'not_found' | 'manual_needed' }>({});
  const [selectedBatchesForShipment, setSelectedBatchesForShipment] = useState<{ [key: number]: BatchInfo[] }>({});
  const [removedItems, setRemovedItems] = useState<Set<number>>(new Set());
  const [shipmentName, setShipmentName] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{ message: string, type: 'success' | 'info' | 'error' } | null>(null);
  const [processedData, setProcessedData] = useState<any[]>([]);

  useEffect(() => {
    checkAuthStatus();
    
    setActiveMainTab('shipments');
    setActiveSubTab('list');
  }, []);

  
  useEffect(() => {
    setProcessedData(getProcessedData());
  }, [rawData, columnMapping, enableBatchTracking, useMmDdYyFormat, removedItems]);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/status`);
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsProcessing(true);

    try {
      const fileData = await fileToBase64(selectedFile);
      
      
      const response = await fetch(`${API_BASE_URL}/v1/inventory-sync/get-columns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileData }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Backend response:', data);
        setColumns(data.columns || []);
        
        
        const fileBuffer = Uint8Array.from(atob(fileData), c => c.charCodeAt(0));
        const workbook = XLSX.read(fileBuffer, { 
          type: 'array',
          cellDates: true,  
          cellNF: false,    
          cellText: false   
        });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,       
          dateNF: 'dd.mm.yyyy' 
        });
        
        
        const normalizedData = jsonData.map(row => {
          const normalizedRow: any = {};
          Object.keys(row as object).forEach(key => {
            const trimmedKey = key.trim();
            if (trimmedKey) {
              normalizedRow[trimmedKey] = (row as any)[key];
            }
          });
          return normalizedRow;
        });
        
        console.log('Frontend parsed data:', normalizedData.slice(0, 3));
        console.log('Frontend parsed data length:', normalizedData.length);
        setRawData(normalizedData);
      }
    } catch (error) {
      console.error('Error processing file:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
    });
  };

  const getProcessedData = () => {
    if (rawData.length === 0 || !columnMapping.itemName) {
      return [];
    }
    
    const processed = rawData.map((row, index) => {
      const rawBatchNumber = enableBatchTracking ? (row[columnMapping.batchNumber] || '') : '';
      
      
      if (enableBatchTracking && rawBatchNumber) {
        console.log(`Row ${index + 1}: Raw batch from Excel: "${rawBatchNumber}" (type: ${typeof rawBatchNumber})`);
        const normalized = normalizeBatchNumber(rawBatchNumber, useMmDdYyFormat);
        console.log(`Row ${index + 1}: Normalized batch: "${normalized}"`);
      }
      
      return {
        rowIndex: index,
        itemName: row[columnMapping.itemName] || '',
        quantity: row[columnMapping.quantity] || '',
        batchNumber: rawBatchNumber,
        normalizedBatchNumber: enableBatchTracking ? normalizeBatchNumber(rawBatchNumber, useMmDdYyFormat) : ''
      };
    }).filter(row => {
      const hasItemName = row.itemName.toString().trim();
      return hasItemName && !removedItems.has(row.rowIndex); 
    });

    return processed;
  };

  const toggleBatches = async (rowIndex: number, itemName: string) => {
    if (!isAuthenticated) {
      alert('Моля влезте в Zoho първо');
      return;
    }

    const newExpanded = new Set(expandedItems);
    
    if (expandedItems.has(rowIndex)) {
      newExpanded.delete(rowIndex);
    } else {
      newExpanded.add(rowIndex);
      if (!itemBatches[rowIndex]) {
        await loadBatches(rowIndex, itemName);
      }
    }
    
    setExpandedItems(newExpanded);
  };

  const loadBatches = async (rowIndex: number, itemName: string) => {
    setLoadingBatches(prev => new Set(prev).add(rowIndex));
    
    try {
      
      const currentRow = processedData.find(row => row.rowIndex === rowIndex);
      const requiredQuantity = currentRow ? parseInt(currentRow.quantity) || 0 : 0;
      
      
      const url = `${API_BASE_URL}/v1/inventory/batches/${encodeURIComponent(itemName)}?requiredQuantity=${requiredQuantity}&includeEmpty=false`;
      console.log(`🎯 API URL with requiredQuantity: ${url}`);
      
      const response = await fetch(url);
      console.log(`🎯 Response Status: ${response.status} ${response.statusText}`);
      const result = await response.json();
      
      console.log(`🎯 Full Response:`, result);
      console.log('🎯 Data Length:', result.data ? result.data.length : 'NO DATA');
      
      if (result.data && Array.isArray(result.data)) {
        console.log('📊 Batch data received:', result.data);
        console.log('📊 Batch numbers:', result.data.map((b: any) => b.batchNumber || b.batch_number));
        
        let filteredBatches = result.data;
        
        
        if (enableBatchTracking && columnMapping.batchNumber) {
          const currentRow = processedData.find(row => row.rowIndex === rowIndex);
          if (currentRow && currentRow.normalizedBatchNumber) {
            const targetBatchRaw = currentRow.batchNumber;
            const targetBatchNormalized = currentRow.normalizedBatchNumber;
            console.log(`🎯 LOOKING FOR: RAW="${targetBatchRaw}" NORMALIZED="${targetBatchNormalized}"`);
            console.log(`🎯 AVAILABLE BATCHES:`, result.data.map((b: any) => `"${b.batchNumber || b.batch_number}"`));
            
            filteredBatches = result.data.filter((batch: any) => {
              const apiBatch = batch.batchNumber || batch.batch_number || '';
              
              const apiBatchNormalized = normalizeBatchNumber(apiBatch, false); 
              console.log(`🔍 COMPARING: API="${apiBatch}"(norm:"${apiBatchNormalized}") vs FILE="${targetBatchRaw}"(norm:"${targetBatchNormalized}") => ${apiBatchNormalized === targetBatchNormalized}`);
              return apiBatchNormalized === targetBatchNormalized;
            });
            
            console.log(`📊 FOUND ${filteredBatches.length} NORMALIZED MATCHES`);
          } else {
            filteredBatches = result.data;
          }
        } else {
          filteredBatches = result.data;
        }
        
        setItemBatches(prev => ({
          ...prev,
          [rowIndex]: filteredBatches
        }));
      } else {
        console.error('❌ Invalid batch data structure:', result);
        setItemBatches(prev => ({
          ...prev,
          [rowIndex]: []
        }));
      }
      
      if (!response.ok && result.message.includes('authenticate')) {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error loading batches:', error);
      setItemBatches(prev => ({ ...prev, [rowIndex]: [] }));
    } finally {
      setLoadingBatches(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowIndex);
        return newSet;
      });
    }
  };

  
  const createShipment = async () => {
    if (!enableBatchTracking || !columnMapping.batchNumber || processedData.length === 0) {
      showSnackbar('За да създадете пратка, трябва да включите следенето на партиди и да имате данни.', 'error');
      return;
    }

    
    const itemsWithoutBatches = processedData.filter(row => {
      const selectedBatches = selectedBatchesForShipment[row.rowIndex] || [];
      return selectedBatches.length === 0;
    });

    if (itemsWithoutBatches.length === 0) {
      
      showSnackbar('🎉 Всички артикули имат избрани партиди! Запазване на пратката...', 'success');
      await saveShipment();
      return;
    }

    setIsCreatingShipment(true);
    setItemsNeedingManualSelection(new Set());
    
    
    const initialStatus: { [key: number]: 'pending' | 'processing' | 'found' | 'not_found' | 'manual_needed' } = {};
    processedData.forEach(row => {
      initialStatus[row.rowIndex] = 'pending';
    });
    setItemProcessingStatus(initialStatus);

    const itemsNeedingManual = new Set<number>();

    
    for (const row of processedData) {
      
      const existingBatches = selectedBatchesForShipment[row.rowIndex] || [];
      if (existingBatches.length > 0) {
        setItemProcessingStatus(prev => ({
          ...prev,
          [row.rowIndex]: 'found'
        }));
        console.log(`✅ Skipping ${row.itemName} - already has ${existingBatches.length} selected batches`);
        continue;
      }

      
      setItemProcessingStatus(prev => ({
        ...prev,
        [row.rowIndex]: 'processing'
      }));

      try {
        
        const requiredQuantity = parseInt(row.quantity) || 0;
        
        
        const url = `${API_BASE_URL}/v1/inventory/batches/${encodeURIComponent(row.itemName)}?requiredQuantity=${requiredQuantity}&includeEmpty=false`;
        console.log(`🎯 Shipment API URL with requiredQuantity: ${url}`);
        
        const response = await fetch(url);
        console.log(`🎯 Shipment Response Status: ${response.status} ${response.statusText}`);
        const result = await response.json();

        console.log(`🎯 Creating shipment for ${row.itemName}`);
        console.log(`🎯 Shipment API Response:`, result);
        console.log(`🎯 Shipment Data Length:`, result.data ? result.data.length : 'NO DATA');

        if (result.data && Array.isArray(result.data)) {
          console.log(`🔍 Found ${result.data.length} batches for ${row.itemName}`);
          console.log('🔍 Batch numbers:', result.data.map((b: any) => b.batchNumber || b.batch_number));
          
          let foundBatches: BatchInfo[] = [];

          
          if (row.normalizedBatchNumber) {
            const targetBatchRaw = row.batchNumber;
            const targetBatchNormalized = row.normalizedBatchNumber;
            console.log(`🎯 SHIPMENT LOOKING FOR: RAW="${targetBatchRaw}" NORMALIZED="${targetBatchNormalized}"`);
            console.log(`🎯 SHIPMENT AVAILABLE:`, result.data.map((b: any) => `"${b.batchNumber || b.batch_number}"`));
            
            foundBatches = result.data.filter((batch: any) => {
              const apiBatch = batch.batchNumber || batch.batch_number || '';
              
              const apiBatchNormalized = normalizeBatchNumber(apiBatch, false); 
              console.log(`🔍 SHIPMENT COMPARING: API="${apiBatch}"(norm:"${apiBatchNormalized}") vs FILE="${targetBatchRaw}"(norm:"${targetBatchNormalized}") => ${apiBatchNormalized === targetBatchNormalized}`);
              if (apiBatchNormalized === targetBatchNormalized) {
                console.log(`✅ SHIPMENT NORMALIZED MATCH FOUND!`);
              }
              return apiBatchNormalized === targetBatchNormalized;
            });
            
            console.log(`🔍 SHIPMENT FOUND ${foundBatches.length} NORMALIZED MATCHES`);
          } else {
            foundBatches = [];
          }

          if (foundBatches.length > 0) {
            
            let remainingQuantity = requiredQuantity;
            const batchesWithSelectedQuantity = foundBatches.map(batch => {
              const availableQuantity = batch.balance_quantity || batch.inQuantity || 0;
              const quantityToTake = Math.min(remainingQuantity, availableQuantity);
              remainingQuantity -= quantityToTake;
              
              console.log(`🔍 Processing batch for shipment:`, {
                batch_in_id: batch.batch_in_id,
                batch_number: batch.batch_number,
                availableQuantity,
                quantityToTake
              });
              
              return {
                ...batch,
                selectedQuantity: quantityToTake,
                
                batch_in_id: batch.batch_in_id
              };
            }).filter(batch => batch.selectedQuantity > 0);
            
            setItemProcessingStatus(prev => ({
              ...prev,
              [row.rowIndex]: 'found'
            }));
            setSelectedBatchesForShipment(prev => ({
              ...prev,
              [row.rowIndex]: batchesWithSelectedQuantity
            }));
          } else {
            
            console.log(`❌ No matching batch found for ${row.itemName}:`, {
              looking_for: row.batchNumber,
              available_batches: result.data.map((b: any) => b.batchNumber || b.batch_number)
            });
            setItemProcessingStatus(prev => ({
              ...prev,
              [row.rowIndex]: 'not_found'
            }));
            itemsNeedingManual.add(row.rowIndex);
            
            
            setItemBatches(prev => ({
              ...prev,
              [row.rowIndex]: result.data
            }));
          }
        } else {
          
          setItemProcessingStatus(prev => ({
            ...prev,
            [row.rowIndex]: 'not_found'
          }));
          itemsNeedingManual.add(row.rowIndex);
        }
      } catch (error) {
        console.error(`Error processing item ${row.itemName}:`, error);
        setItemProcessingStatus(prev => ({
          ...prev,
          [row.rowIndex]: 'not_found'
        }));
        itemsNeedingManual.add(row.rowIndex);
      }

      
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setItemsNeedingManualSelection(itemsNeedingManual);
    setIsCreatingShipment(false);

    if (itemsNeedingManual.size === 0) {
      
      showSnackbar('🎉 Всички партиди са намерени! Запазване на пратката...', 'success');
      await saveShipment();
    } else {
      
      showSnackbar(`⚠️ ${itemsNeedingManual.size} артикула се нуждаят от ръчен избор на партида.`, 'info');
    }
  };

  
  const checkAndSaveShipment = async () => {
    const itemsWithoutBatches = processedData.filter(row => {
      const selectedBatches = selectedBatchesForShipment[row.rowIndex] || [];
      return selectedBatches.length === 0;
    });

    if (itemsWithoutBatches.length === 0 && processedData.length > 0) {
      
      showSnackbar('🎉 Всички артикули са готови! Запазване на пратката...', 'success');
      await saveShipment();
    }
  };

  const saveShipment = async () => {
    setIsCreatingShipment(true);

    console.log('🔍 Debug saveShipment:');
    console.log('processedData:', processedData);
    console.log('selectedBatchesForShipment:', selectedBatchesForShipment);

    const shipmentItems = processedData
      .filter(row => !removedItems.has(row.rowIndex)) 
      .map(row => {
        const selectedBatches = selectedBatchesForShipment[row.rowIndex] || [];
        console.log(`🔍 Row ${row.rowIndex} (${row.itemName}):`, {
          selectedBatches,
          batchesLength: selectedBatches.length,
          isRemoved: removedItems.has(row.rowIndex)
        });

        console.log(`🔍 Creating shipment item for ${row.itemName}:`, {
          selectedBatches,
          selectedBatchesLength: selectedBatches.length,
          firstBatchKeys: selectedBatches[0] ? Object.keys(selectedBatches[0]) : [],
          firstBatchInId: selectedBatches[0] ? selectedBatches[0].batch_in_id : 'NO_BATCHES'
        });
        
        return {
          item_name: row.itemName,
          required_quantity: parseInt(row.quantity) || 0,
          batches: selectedBatches.map((batch, index) => {
            
            const batchId = batch.batch_in_id || '';
            
            console.log(`🔍 Extracting batch_id:`, {
              batch_in_id: batch.batch_in_id,
              batchInId: batch.batchInId,
              batch_id: (batch as any).batch_id,
              batchId: (batch as any).batchId,
              id: (batch as any).id,
              final_batchId: batchId,
              all_keys: Object.keys(batch),
              full_batch: batch
            });
            
            const mappedBatch = {
              zoho_batch_id: batchId,
              zoho_batch_number: batch.batch_number || batch.batchNumber || '',
              quantity_selected: batch.selectedQuantity || batch.balance_quantity || batch.inQuantity || 0,
              storage_locations: (batch.storages || []).map(storage => ({
                zoho_storage_id: storage.storage_id || storage.bin_id || '',
                storage_name: storage.bin_name || storage.storage_name || '',
                warehouse_id: storage.warehouse_id || '',
                warehouse_name: storage.warehouse_name || '',
                quantity: storage.optimized_quantity || batch.selectedQuantity || storage.in_quantity || storage.quantity || 1,
              })),
            };
            
            console.log(`🔍 Mapped batch ${index} for shipment:`, {
              original_batch_in_id: batch.batch_in_id,
              original_batchInId: batch.batchInId,
              final_zoho_batch_id: mappedBatch.zoho_batch_id,
              batch_keys: Object.keys(batch)
            });
            
            return mappedBatch;
          }),
        };
      })
      .filter(item => item.batches.length > 0); 

    console.log('🔍 Final shipmentItems:', shipmentItems);

    if (shipmentItems.length === 0) {
      console.log('❌ No items with batches found!');
      console.log('❌ Available data:');
      console.log('  - processedData.length:', processedData.length);
      console.log('  - selectedBatchesForShipment keys:', Object.keys(selectedBatchesForShipment));
      console.log('  - removedItems:', Array.from(removedItems));
      
      
      processedData.forEach((row, index) => {
        const selectedBatches = selectedBatchesForShipment[row.rowIndex] || [];
        const isRemoved = removedItems.has(row.rowIndex);
        console.log(`  - Row ${index} (rowIndex: ${row.rowIndex}, ${row.itemName}): ${selectedBatches.length} batches selected, removed: ${isRemoved}`);
      });
      
      
      const itemsWithPotentialBatches = processedData.filter(row => {
        const selectedBatches = selectedBatchesForShipment[row.rowIndex] || [];
        return !removedItems.has(row.rowIndex) && selectedBatches.length > 0;
      });
      
      console.log('❌ Items with potential batches:', itemsWithPotentialBatches.length);
      
      
      setIsCreatingShipment(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: shipmentName.trim() || undefined,
          items: shipmentItems 
        }),
      });

      if (response.ok) {
        const result = await response.json();
        showSnackbar(`✅ Пратка ${result.shipmentId} е създадена успешно!`, 'success');
        resetAllFields(); 
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Неуспешно създаване на пратка');
      }
    } catch (error: any) {
      showSnackbar(`❌ Грешка: ${error.message}`, 'error');
    } finally {
      setIsCreatingShipment(false);
    }
  };

  
  const selectBatchForShipment = (rowIndex: number, batch: BatchInfo) => {
    const currentRow = processedData.find(row => row.rowIndex === rowIndex);
    if (!currentRow) return;

    const requiredQuantity = parseInt(currentRow.quantity) || 0;
    const selectedBatches = selectedBatchesForShipment[rowIndex] || [];
    const batchQuantity = batch.balance_quantity || batch.inQuantity || 0;
    
    
    const currentTotal = selectedBatches.reduce((total, selectedBatch) => {
      return total + (selectedBatch.selectedQuantity || selectedBatch.balance_quantity || selectedBatch.inQuantity || 0);
    }, 0);
    
    
    const remainingNeeded = requiredQuantity - currentTotal;
    const quantityToTakeFromBatch = Math.min(remainingNeeded, batchQuantity);
    
    
    console.log(`🔍 Manual batch selection:`, {
      batch_in_id: batch.batch_in_id,
      batch_number: batch.batch_number,
      quantityToTakeFromBatch
    });
    
    const batchWithSelectedQuantity = {
      ...batch,
      selectedQuantity: quantityToTakeFromBatch,
      
      batch_in_id: batch.batch_in_id
    };
    
    
    const newBatches = [...selectedBatches, batchWithSelectedQuantity];
    const newTotal = currentTotal + quantityToTakeFromBatch;
    
    setSelectedBatchesForShipment(prev => ({
      ...prev,
      [rowIndex]: newBatches
    }));
    
    
    if (newTotal >= requiredQuantity) {
      setItemProcessingStatus(prev => ({
        ...prev,
        [rowIndex]: 'found'
      }));
      
      
      setItemsNeedingManualSelection(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowIndex);
        return newSet;
      });
      
      
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowIndex);
        return newSet;
      });
      
      if (newTotal > requiredQuantity) {
        
        const excess = newTotal - requiredQuantity;
        showSnackbar(`✅ Достатъчно количество избрано! Излишък: ${excess} бр.`, 'success');
      } else {
        
        showSnackbar(`✅ Отлично! Избраното количество точно отговаря на нуждата.`, 'success');
      }
      
      
      setTimeout(() => checkAndSaveShipment(), 500);
    } else {
      
      const remaining = requiredQuantity - newTotal;
      showSnackbar(`⚠️ Остава да изберете: ${remaining} бр.`, 'info');
    }
  };

  
  const removeBatchFromShipment = (rowIndex: number, batchToRemove: BatchInfo) => {
    const selectedBatches = selectedBatchesForShipment[rowIndex] || [];
    const newBatches = selectedBatches.filter(batch => 
      batch.batchNumber !== batchToRemove.batchNumber && 
      batch.batch_number !== batchToRemove.batch_number
    );
    
    setSelectedBatchesForShipment(prev => ({
      ...prev,
      [rowIndex]: newBatches
    }));
    
    
    if (newBatches.length === 0) {
      setItemProcessingStatus(prev => ({
        ...prev,
        [rowIndex]: 'not_found'
      }));
      
      setItemsNeedingManualSelection(prev => new Set(prev).add(rowIndex));
    }
  };

  
  const getQuantityInfo = (rowIndex: number) => {
    const currentRow = processedData.find(row => row.rowIndex === rowIndex);
    if (!currentRow) return { required: 0, selected: 0, remaining: 0 };

    const requiredQuantity = parseInt(currentRow.quantity) || 0;
    const selectedBatches = selectedBatchesForShipment[rowIndex] || [];
    const selectedTotal = selectedBatches.reduce((total, batch) => {
      
      return total + (batch.selectedQuantity || batch.balance_quantity || batch.inQuantity || 0);
    }, 0);
    
    return {
      required: requiredQuantity,
      selected: selectedTotal,
      remaining: Math.max(0, requiredQuantity - selectedTotal)
    };
  };

  const removeItem = (rowIndex: number) => {
    setRemovedItems(prev => new Set(prev).add(rowIndex));
  };

  const resetAllFields = () => {
    
    setFile(null);
    setRawData([]);
    setColumns([]);
    setProcessedData([]);
    
    
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    
    setColumnMapping({
      itemName: '',
      quantity: '',
      batchNumber: ''
    });
    
    
    setSelectedBatchesForShipment({});
    setItemProcessingStatus({});
    setItemsNeedingManualSelection(new Set());
    setRemovedItems(new Set());
    setItemBatches({});
    setExpandedItems(new Set());
    setLoadingBatches(new Set());
    setShipmentName('');
    
    
    setIsProcessing(false);
    setIsCreatingShipment(false);
    
    
    setEnableBatchTracking(false);
    setUseMmDdYyFormat(false);
  };

  const showSnackbar = (message: string, type: 'success' | 'info' | 'error' = 'info') => {
    setSnackbar({ message, type });
    setTimeout(() => setSnackbar(null), 4000); 
  };

  const showData = columnMapping.itemName && processedData.length > 0;
  
  console.log('showData:', showData, '| processedData.length:', processedData.length);

  return (
    <div className="automation-container">
      <div className="automation-header">
        <h2>Автоматизация</h2>
      </div>

      {}
      <nav className="automation-main-nav">
        <button 
          className={`main-nav-button ${activeMainTab === 'shipments' ? 'active' : ''}`}
          onClick={() => {
            setActiveMainTab('shipments');
            setActiveSubTab('list');
          }}
        >
          Пратки
        </button>
        <button 
          className={`main-nav-button ${activeMainTab === 'deliveries' ? 'active' : ''}`}
          onClick={() => {
            setActiveMainTab('deliveries');
            setActiveSubTab('list');
          }}
        >
          Доставки
        </button>
      </nav>

      {}
      <nav className="automation-sub-nav">
        <button 
          className={`sub-nav-button ${activeSubTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('list')}
        >
          Всички
        </button>
        <button 
          className={`sub-nav-button ${activeSubTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('create')}
        >
          {activeMainTab === 'shipments' ? 'Създаване на пратка' : 'Създаване на доставка'}
        </button>
      </nav>

      {}
      {activeMainTab === 'shipments' && activeSubTab === 'create' && (
        <div className="automation-content">
            {}
            <div className="upload-section">
              <div className="file-upload-area">
                <label htmlFor="file-input" className={`file-upload-label ${file ? 'has-file' : ''}`}>
                  <div className="upload-icon">📄</div>
                  <div className="upload-text">
                    {file ? (
                      <>
                        <strong>{file.name}</strong>
                        <small>{(file.size / 1024).toFixed(1)} KB</small>
                      </>
                    ) : (
                      <>
                        <strong>Изберете Excel файл</strong>
                        <small>.xlsx, .xls</small>
                      </>
                    )}
                  </div>
                </label>
                <input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="file-input"
                />
              </div>
              
              {isProcessing && (
                <div className="processing-indicator">
                  <div className="spinner"></div>
                  <span>Обработване...</span>
                </div>
              )}
            </div>

            {columns.length > 0 && (
              <div className="column-mapping-section">
                <h3>Дефиниране на колони</h3>
                <div className="mapping-grid">
                  <div className="mapping-field">
                    <label>
                      <span className="required">*</span> Име на стока:
                    </label>
                    <select
                      value={columnMapping.itemName}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, itemName: e.target.value }))}
                      className="column-select"
                    >
                      <option value="">-- Изберете колона --</option>
                      {columns.map((column, index) => (
                        <option key={index} value={column}>{column}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mapping-field">
                    <label>
                      <span className="required">*</span> Количество:
                    </label>
                    <select
                      value={columnMapping.quantity}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, quantity: e.target.value }))}
                      className="column-select"
                    >
                      <option value="">-- Изберете колона --</option>
                      {columns.map((column, index) => (
                        <option key={index} value={column}>{column}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mapping-field">
                    <label>
                      Номер на партида:
                    </label>
                    <select
                      value={columnMapping.batchNumber}
                      onChange={(e) => {
                        setColumnMapping(prev => ({ ...prev, batchNumber: e.target.value }));
                        setEnableBatchTracking(!!e.target.value);
                      }}
                      className="column-select"
                    >
                      <option value="">-- Изберете колона --</option>
                      {columns.map((column, index) => (
                        <option key={index} value={column}>{column}</option>
                      ))}
                    </select>
                  </div>

                  {enableBatchTracking && (
                    <div className="mapping-field">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={useMmDdYyFormat}
                          onChange={(e) => setUseMmDdYyFormat(e.target.checked)}
                          className="date-format-checkbox"
                        />
                        Използвай MM.DD.YY формат (вместо DD.MM.YY)
                      </label>
                      <small className="format-help">
                        🔸 Неотметнато: 17/06/25 → 17.06.25 (ден.месец.година)<br/>
                        🔸 Отметнато: 06/17/25 → 17.06.25 (месец.ден.година)
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {}
            {showData && (
              <div className="data-preview-section">
                <h3>Данни ({processedData.length} реда)</h3>
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Стока</th>
                        <th>Количество</th>
                        {enableBatchTracking && <th>Партида</th>}
                        {enableBatchTracking && <th>Статус</th>}
                        <th>Действия</th>
                        <th>Премахни</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedData.map((row) => (
                        <React.Fragment key={row.rowIndex}>
                          <tr className={`data-row ${itemsNeedingManualSelection.has(row.rowIndex) ? 'needs-manual-selection' : ''} ${itemProcessingStatus[row.rowIndex] === 'found' ? 'batch-found' : ''}`}>
                            <td>{row.itemName}</td>
                            <td>{row.quantity}</td>
                            {enableBatchTracking && <td>{row.normalizedBatchNumber}</td>}
                            {enableBatchTracking && (
                              <td>
                                {itemProcessingStatus[row.rowIndex] === 'pending' && (
                                  <span className="status-pending">⏳ Чакащ</span>
                                )}
                                {itemProcessingStatus[row.rowIndex] === 'processing' && (
                                  <span className="status-processing">🔄 Обработване</span>
                                )}
                                {itemProcessingStatus[row.rowIndex] === 'found' && (
                                  <span className="status-found">✅ Намерена</span>
                                )}
                                {itemProcessingStatus[row.rowIndex] === 'not_found' && (
                                  <span className="status-not-found">❌ Не намерена</span>
                                )}
                                {!itemProcessingStatus[row.rowIndex] && (
                                  <span className="status-none">-</span>
                                )}
                              </td>
                            )}
                            <td>
                              <button
                                onClick={() => toggleBatches(row.rowIndex, row.itemName)}
                                className="batch-button"
                                disabled={loadingBatches.has(row.rowIndex)}
                              >
                                {loadingBatches.has(row.rowIndex)
                                    ? 'Зареждане...'
                                    : expandedItems.has(row.rowIndex)
                                    ? 'Скрий партиди'
                                    : itemsNeedingManualSelection.has(row.rowIndex)
                                    ? 'Покажи други партиди'
                                    : (enableBatchTracking && row.normalizedBatchNumber
                                        ? `Покажи партида ${row.normalizedBatchNumber}`
                                        : 'Покажи партиди')}
                              </button>
                            </td>
                            <td>
                              <button onClick={() => removeItem(row.rowIndex)} className="remove-item-button">
                                ❌
                              </button>
                            </td>
                          </tr>
                          {expandedItems.has(row.rowIndex) && (
                            <tr className="batch-expansion">
                              <td colSpan={enableBatchTracking ? 6 : 4}>
                                <div className="batch-details">
                                  {itemBatches[row.rowIndex]?.length > 0 ? (
                                    <div className="batch-list">
                                      <h4>
                                        {enableBatchTracking && row.normalizedBatchNumber 
                                          ? `Партида ${row.normalizedBatchNumber} за "${row.itemName}" (${itemBatches[row.rowIndex].length} намерени):`
                                          : `Партиди за "${row.itemName}":` 
                                        }
                                        {itemsNeedingManualSelection.has(row.rowIndex) && (
                                          <span className="manual-selection-indicator"> ⚠️ Изберете партида</span>
                                        )}
                                      </h4>
                                      
                                      {itemsNeedingManualSelection.has(row.rowIndex) && (
                                        <div className="quantity-summary">
                                          {(() => {
                                            const quantityInfo = getQuantityInfo(row.rowIndex);
                                            return (
                                              <div className="quantity-info">
                                                <span className="required">Нужно: <strong>{quantityInfo.required} бр.</strong></span>
                                                <span className="selected">Избрано: <strong>{quantityInfo.selected} бр.</strong></span>
                                                {quantityInfo.remaining > 0 && (
                                                  <span className="remaining">Остава: <strong>{quantityInfo.remaining} бр.</strong></span>
                                                )}
                                                {quantityInfo.selected > quantityInfo.required && (
                                                  <span className="excess">Излишък: <strong>{quantityInfo.selected - quantityInfo.required} бр.</strong></span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      )}
                                      
                                      {selectedBatchesForShipment[row.rowIndex] && selectedBatchesForShipment[row.rowIndex].length > 0 && (
                                        <div className="selected-batches">
                                          <h5>Избрани партиди:</h5>
                                          {selectedBatchesForShipment[row.rowIndex].map((selectedBatch, index) => (
                                            <div key={index} className="selected-batch-item">
                                              <span className="batch-info">
                                                📦 {normalizeBatchNumber(selectedBatch.batchNumber || selectedBatch.batch_number || 'N/A')} 
                                                ({selectedBatch.selectedQuantity || selectedBatch.balance_quantity || selectedBatch.inQuantity || 0} бр.)
                                                {selectedBatch.selectedQuantity && (
                                                  <small> от общо {selectedBatch.balance_quantity || selectedBatch.inQuantity || 0} бр.</small>
                                                )}
                                              </span>
                                              <button
                                                onClick={() => removeBatchFromShipment(row.rowIndex, selectedBatch)}
                                                className="remove-batch-button"
                                              >
                                                ❌ Премахни
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {itemBatches[row.rowIndex]
                                        .sort((a, b) => {
                                          
                                          const dateA = normalizeBatchNumber(a.batchNumber || a.batch_number || '');
                                          const dateB = normalizeBatchNumber(b.batchNumber || b.batch_number || '');
                                          
                                          
                                          if (dateA.match(/^\d{2}\.\d{2}\.\d{2}$/) && dateB.match(/^\d{2}\.\d{2}\.\d{2}$/)) {
                                            const [dayA, monthA, yearA] = dateA.split('.').map(num => parseInt(num));
                                            const [dayB, monthB, yearB] = dateB.split('.').map(num => parseInt(num));
                                            
                                            
                                            const fullYearA = yearA <= 30 ? 2000 + yearA : 1900 + yearA;
                                            const fullYearB = yearB <= 30 ? 2000 + yearB : 1900 + yearB;
                                            
                                            const fullDateA = new Date(fullYearA, monthA - 1, dayA);
                                            const fullDateB = new Date(fullYearB, monthB - 1, dayB);
                                            
                                            return fullDateA.getTime() - fullDateB.getTime();
                                          }
                                          
                                          
                                          return dateA.localeCompare(dateB);
                                        })
                                        .map((batch, index) => (
                                        <div key={index} className="batch-item">
                                          <div><strong>Номер:</strong> {normalizeBatchNumber(batch.batchNumber || batch.batch_number || 'N/A')}</div>
                                          <div><strong>Налично количество:</strong> {batch.balance_quantity || batch.inQuantity || 0} бр.</div>
                                          
                                          {batch.storages && batch.storages.length > 0 ? (
                                            <div className="storage-info">
                                              <strong>Местоположения по складове:</strong>
                                              <div className="storage-list">
                                                {batch.storages.map((storage, storageIndex) => (
                                                  <div key={storageIndex} className={`storage-item ${storage.error ? 'error' : ''}`}>
                                                    <span className="bin-name">📦 {storage.bin_name || 'N/A'}</span>
                                                    {storage.warehouse_name && (
                                                      <span className="warehouse-name"> 🏢 {storage.warehouse_name}</span>
                                                    )}
                                                    {storage.quantity && (
                                                      <span className="bin-quantity"> ({storage.quantity} бр.)</span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="no-storage">
                                              <p style={{color: '#666', fontStyle: 'italic'}}>Няма информация за местоположение</p>
                                            </div>
                                          )}
                                          
                                          {itemsNeedingManualSelection.has(row.rowIndex) && (
                                            <div className="manual-selection">
                                              {(() => {
                                                const isAlreadySelected = selectedBatchesForShipment[row.rowIndex]?.some((selectedBatch) => 
                                                  (selectedBatch.batchNumber === batch.batchNumber ||
                                                   selectedBatch.batch_number === batch.batch_number)
                                                );
                                                const batchQuantity = batch.balance_quantity || batch.inQuantity || 0;
                                                
                                                if (isAlreadySelected) {
                                                  return (
                                                    <button className="select-batch-button selected" disabled>
                                                      ✅ Вече избрана
                                                    </button>
                                                  );
                                                }
                                                
                                                return (
                                                  <button
                                                    onClick={() => selectBatchForShipment(row.rowIndex, batch)}
                                                    className="select-batch-button"
                                                  >
                                                    📦 Избери ({batchQuantity} бр.)
                                                  </button>
                                                );
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="no-batches">
                                      <p>
                                        {enableBatchTracking && row.normalizedBatchNumber 
                                          ? `Няма партида ${row.normalizedBatchNumber} за тази стока`
                                          : 'Няма партиди за тази стока'
                                        }
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {enableBatchTracking && processedData.length > 0 && (
                  <div className="shipment-creation">
                    <div className="shipment-name-input">
                      <label htmlFor="shipment-name">Име на пратката (незадължително):</label>
                      <input
                        id="shipment-name"
                        type="text"
                        value={shipmentName}
                        onChange={(e) => setShipmentName(e.target.value)}
                        placeholder="Оставете празно за автоматично име"
                        className="form-input"
                        disabled={isCreatingShipment}
                      />
                    </div>
                    <button
                      onClick={createShipment}
                      className="create-shipment-button"
                      disabled={isCreatingShipment}
                    >
                      {isCreatingShipment ? '🔄 Обработване...' : 
                        processedData.every(row => (selectedBatchesForShipment[row.rowIndex] || []).length > 0) 
                          ? '💾 Запази пратката' 
                          : '📦 Създай пратка'}
                    </button>
                    
                    {isCreatingShipment && (
                      <div className="processing-info">
                        <p>Обработване на партиди...</p>
                      </div>
                    )}
                    
                    {!isCreatingShipment && Object.keys(itemProcessingStatus).length > 0 && (
                      <div className="shipment-summary">
                        <h4>Резултат от обработката:</h4>
                        <ul>
                          <li>✅ Намерени: {Object.values(itemProcessingStatus).filter(status => status === 'found').length}</li>
                          <li>❌ Не намерени: {Object.values(itemProcessingStatus).filter(status => status === 'not_found').length}</li>
                          <li>📝 Общо артикули: {processedData.length}</li>
                        </ul>
                        {itemsNeedingManualSelection.size > 0 && (
                          <p className="manual-selection-notice">
                            ⚠️ Изберете партиди за артикулите, отбелязани с "❌ Не намерена"
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeMainTab === 'shipments' && activeSubTab === 'list' && (
          <ShipmentList />
        )}

        {activeMainTab === 'deliveries' && activeSubTab === 'list' && (
          <DeliveryList />
        )}

        {activeMainTab === 'deliveries' && activeSubTab === 'create' && (
          <div className="automation-content">
            {}
            <div className="upload-section">
              <div className="file-upload-area">
                <label htmlFor="delivery-file-input" className={`file-upload-label ${file ? 'has-file' : ''}`}>
                  <div className="upload-icon">📦</div>
                  <div className="upload-text">
                    {file ? (
                      <>
                        <strong>{file.name}</strong>
                        <small>{(file.size / 1024).toFixed(1)} KB</small>
                      </>
                    ) : (
                      <>
                        <strong>Изберете Excel файл за доставка</strong>
                        <small>.xlsx, .xls</small>
                      </>
                    )}
                  </div>
                </label>
                <input
                  id="delivery-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="file-input"
                />
              </div>
              
              {isProcessing && (
                <div className="processing-indicator">
                  <div className="spinner"></div>
                  <span>Обработване...</span>
                </div>
              )}
            </div>

            {columns.length > 0 && (
              <div className="column-mapping-section">
                <h3>Дефиниране на колони за доставка</h3>
                <div className="mapping-grid">
                  <div className="mapping-field">
                    <label>
                      <span className="required">*</span> Име на стока:
                    </label>
                    <select
                      value={columnMapping.itemName}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, itemName: e.target.value }))}
                      className="column-select"
                    >
                      <option value="">-- Изберете колона --</option>
                      {columns.map((column, index) => (
                        <option key={index} value={column}>{column}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mapping-field">
                    <label>
                      <span className="required">*</span> Количество:
                    </label>
                    <select
                      value={columnMapping.quantity}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, quantity: e.target.value }))}
                      className="column-select"
                    >
                      <option value="">-- Изберете колона --</option>
                      {columns.map((column, index) => (
                        <option key={index} value={column}>{column}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mapping-field">
                    <label>
                      Номер на партида:
                    </label>
                    <select
                      value={columnMapping.batchNumber}
                      onChange={(e) => {
                        setColumnMapping(prev => ({ ...prev, batchNumber: e.target.value }));
                        setEnableBatchTracking(!!e.target.value);
                      }}
                      className="column-select"
                    >
                      <option value="">-- Изберете колона --</option>
                      {columns.map((column, index) => (
                        <option key={index} value={column}>{column}</option>
                      ))}
                    </select>
                  </div>

                  {enableBatchTracking && (
                    <div className="mapping-field">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={useMmDdYyFormat}
                          onChange={(e) => setUseMmDdYyFormat(e.target.checked)}
                          className="date-format-checkbox"
                        />
                        Използвай MM.DD.YY формат (вместо DD.MM.YY)
                      </label>
                      <small className="format-help">
                        🔸 Неотметнато: 17/06/25 → 17.06.25 (ден.месец.година)<br/>
                        🔸 Отметнато: 06/17/25 → 17.06.25 (месец.ден.година)
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {}
            {showData && (
              <div className="data-preview-section">
                <h3>Данни за доставка ({processedData.length} реда)</h3>
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Стока</th>
                        <th>Количество</th>
                        {enableBatchTracking && <th>Партида</th>}
                        <th>Премахни</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedData.map((row) => (
                        <tr key={row.rowIndex} className="data-row">
                          <td>{row.itemName}</td>
                          <td>{row.quantity}</td>
                          {enableBatchTracking && <td>{row.normalizedBatchNumber}</td>}
                          <td>
                            <button onClick={() => removeItem(row.rowIndex)} className="remove-item-button">
                              ❌
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {processedData.length > 0 && (
                  <div className="shipment-creation">
                    <div className="shipment-name-input">
                      <label htmlFor="delivery-name">Име на доставката (незадължително):</label>
                      <input
                        id="delivery-name"
                        type="text"
                        value={shipmentName}
                        onChange={(e) => setShipmentName(e.target.value)}
                        placeholder="Оставете празно за автоматично име"
                        className="form-input"
                        disabled={isCreatingShipment}
                      />
                    </div>
                    <button
                      onClick={() => {
                        
                        console.log('Creating delivery with data:', processedData);
                        alert('Функционалността за доставки ще бъде добавена скоро!');
                      }}
                      className="create-shipment-button"
                      disabled={isCreatingShipment}
                      style={{ backgroundColor: '#28a745' }}
                    >
                      {isCreatingShipment ? '🔄 Обработване...' : '📦 Създай доставка'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      {snackbar && (
        <div className={`snackbar ${snackbar.type}`}>
          {snackbar.message}
        </div>
      )}
    </div>
  );
};

export default Automation; 